import { ADDRESS_FORMATS_BY_COUNTRY, DEFAULT_ADDRESS_FORMAT } from "./addressFormats";

/** Structural subset shared by `AddressType` and the generated `AddressFragment`. */
export interface FormattableAddress {
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  phone?: string | null;
  streetAddress1?: string | null;
  streetAddress2?: string | null;
  cityArea?: string | null;
  city?: string | null;
  countryArea?: string | null;
  postalCode?: string | null;
  country?: { code?: string | null; country?: string | null } | null;
}

type FieldToken = "N" | "O" | "A" | "D" | "C" | "S" | "Z" | "X";

const FIELD_TOKEN_PATTERN = /(%[NOADCSZX])/;
const LINE_SEPARATOR = "%n";

const isFieldToken = (part: string): boolean => FIELD_TOKEN_PATTERN.test(part) && part.length === 2;

const clean = (value: string | null | undefined): string => (value ?? "").trim();

const splitLines = (value: string): string[] =>
  value
    .split("\n")
    .map(line => line.trim())
    .filter(Boolean);

export const getAddressFormat = (countryCode: string | null | undefined): string =>
  (countryCode && ADDRESS_FORMATS_BY_COUNTRY[countryCode.toUpperCase()]) || DEFAULT_ADDRESS_FORMAT;

const getLayoutValues = (address: FormattableAddress): Record<FieldToken, string> => ({
  // Name and organization are rendered in the contact block ahead of the layout
  // (see getAddressContactLines), and Saleor does not store sorting codes.
  N: "",
  O: "",
  X: "",
  A: [clean(address.streetAddress1), clean(address.streetAddress2)].filter(Boolean).join("\n"),
  D: clean(address.cityArea),
  C: clean(address.city),
  S: clean(address.countryArea),
  Z: clean(address.postalCode),
});

/**
 * Renders one `%n`-delimited line of a libaddressinput layout.
 *
 * Literal text (", ", "-", "〒", "CH-") is only kept when it joins two present fields
 * or prefixes the first present field, so an empty state or postal code never leaves
 * a dangling separator behind. A line made only of literals (e.g. "GUERNSEY") is kept.
 */
const formatLayoutLine = (lineFormat: string, values: Record<FieldToken, string>): string => {
  const parts = lineFormat.split(FIELD_TOKEN_PATTERN).filter(Boolean);

  if (!parts.some(isFieldToken)) {
    return lineFormat.trim();
  }

  let line = "";
  let pendingLiteral = "";
  let skippedField = false;

  for (const part of parts) {
    if (!isFieldToken(part)) {
      pendingLiteral += part;
      continue;
    }

    const value = values[part.charAt(1) as FieldToken];

    if (!value) {
      skippedField = true;
      pendingLiteral = "";
      continue;
    }

    if (line || !skippedField) {
      line += pendingLiteral;
    }

    line += value;
    pendingLiteral = "";
  }

  return line.trim();
};

/** Name, phone and company, in that order, skipping empty values. */
export const getAddressContactLines = (address: FormattableAddress): string[] =>
  [
    [clean(address.firstName), clean(address.lastName)].filter(Boolean).join(" "),
    clean(address.phone),
    clean(address.companyName),
  ].filter(Boolean);

/** Street, locality and postal lines in the destination country's layout, then the country name. */
export const getAddressLayoutLines = (address: FormattableAddress): string[] => {
  const values = getLayoutValues(address);
  const layoutLines = getAddressFormat(address.country?.code)
    .split(LINE_SEPARATOR)
    .map(lineFormat => formatLayoutLine(lineFormat, values));

  return [...layoutLines, clean(address.country?.country)].flatMap(splitLines);
};

export const formatAddressLines = (address: FormattableAddress): string[] => [
  ...getAddressContactLines(address),
  ...getAddressLayoutLines(address),
];

export const formatAddressText = (address: FormattableAddress): string =>
  formatAddressLines(address).join("\n");
